// @ts-nocheck
/**
 * DashboardPage — routes traffic to one of two entirely different UIs:
 *
 *   1. Agents AND team leads (role_ccagent, role_ccteamlead, or an agent
 *      record with access 'team_lead') → AgentPortalPage (the /cti2
 *      workspace). It renders TeamDashboardView for team leads and
 *      AgentDashboardView for agents. These roles must never see the
 *      admin widget grid. AgentPortalPage owns its own socket lifecycle
 *      (useSocket/useMobileCallSocket) internally, since it was built to
 *      also work standalone at the /cti2 route — nothing extra is wired
 *      up here.
 *
 *   2. Everyone else → DashboardShell, the obd3-gui-v1-style widget
 *      grid with per-user layouts, drag-to-reorder, size cycler, and
 *      the Add-widget slide-over library.
 *
 * The role is re-read from storage on every render and on every session
 * rewrite (session refresh fires 'acl-refreshed', other tabs fire
 * 'storage'). It used to be frozen at mount, so a session whose stored
 * user changed after mount stayed on the admin shell.
 *
 * All the shared state that a couple of widgets need (date range,
 * portal-report bootstrap, agent-status polling) lives in
 * DashboardDataContext, hoisted from the legacy DashboardPage's inline
 * state so behavior stays byte-identical to v1.
 */
import { useEffect, useReducer } from 'react';
import { getUser } from '@/utilities/setting';
import { USER_ROLES } from '@/utilities/constant';
import AgentPortalPage from '@/features/cti2/AgentPortalPage';
import { isTeamLeadUser } from '@/features/cti2/acl';
import { DashboardDataProvider } from './DashboardDataContext';
import { DashboardShell } from './components/DashboardShell';

const PORTAL_ROLES = [USER_ROLES.CALL_CENTRE_AGENT, USER_ROLES.CALL_CENTRE_TEAM_LEAD];

export function isAgentPortalUser(user) {
  return PORTAL_ROLES.includes(user?.role) || isTeamLeadUser(user);
}

export function DashboardPage() {
  const [, rerender] = useReducer((n) => n + 1, 0);

  useEffect(() => {
    window.addEventListener('acl-refreshed', rerender);
    window.addEventListener('storage', rerender);
    return () => {
      window.removeEventListener('acl-refreshed', rerender);
      window.removeEventListener('storage', rerender);
    };
  }, []);

  if (isAgentPortalUser(getUser())) {
    return <AgentPortalPage />;
  }

  return (
    <DashboardDataProvider>
      <DashboardShell />
    </DashboardDataProvider>
  );
}

export default DashboardPage;
